import { useEffect, useState } from "react";

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import { grey } from '@mui/material/colors';
import { cloneDeep, isEmpty, isEqual } from "lodash";
import { withTranslation } from "react-i18next";

import authService from "@plannel/services/system/auth-service";
import userService from "@plannel/services/system/user-service";
import PasswordPolicyContainer from "@plannel/components/PasswordPolicyContainer";
import PasswordInput from "@plannel/components/PasswordInput";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import SelectExpTime from "@plannel/components/SelectExpTime";
import StyledSwitch from "@plannel/components/ui/StyledSwitch"
import AppCard, { CARD_HEADER_SX } from "@plannel/components/ui/AppCard";
import axiosUtil from "@plannel/utils/axios-bigint";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import p13nService from "@plannel/services/system/p13n-service";

// 차트 팔레트 선호 저장 위치(사용자 전역 p13n) — 페이지별 chart 설정과 충돌하지 않는 별도 pageCd.
const PALETTE_PAGE_CD = "PERSONAL_SETTING";

const PersonalSettings = ({ t }) => {
  const [currentUser, setCurrentUser] = useState(authService.getCurrentUser());
  const username = currentUser?.username ?? "";
  const [userRoles, setUserRoles] = useState([]);

  const [firstName, setFirstName] = useState(currentUser?.firstName ?? "");
  const [lastName, setLastName] = useState(currentUser?.lastName ?? "");
  const [dispName, setDispName] = useState(currentUser?.displayName ?? "");
  const [deptName, setDeptName] = useState(currentUser?.deptName ?? "");
  const [jwtExpireSec, setJwtExpireSec] = useState(currentUser?.jwtExpireSec ?? "");

  const [changePassword, setChangePassword] = useState(false);
  const [showPassword, setShowPassword] = useState({ newPassword: false, confirmPassword: false });
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const { snackBarMessageColor } = ColorUtil;

  // 차트 팔레트 선호 — 선택 즉시 적용 + localStorage 캐시 + p13n 저장(별도 Save 버튼 없음).
  const [chartPalette, setChartPalette] = useState(ColorUtil.getActivePalette());
  const choosePalette = (id) => {
    if (id === chartPalette) return;
    setChartPalette(id);
    ColorUtil.setActivePalette(id);
    try {
      const s = JSON.parse(localStorage.getItem("settings") || "{}");
      s.chartPalette = id;
      localStorage.setItem("settings", JSON.stringify(s));
    } catch (e) { debug(e); }
    p13nService.upsert({ pageCd: PALETTE_PAGE_CD, configObj: { chartPalette: id } })
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch((error) => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(error);
      });
  };
  const saveUserInfo = () => {
    userService.changeUserInfo(username, firstName, lastName, dispName, deptName, jwtExpireSec)
      .then(
        (response) => {
          const user = cloneDeep(currentUser);
          user.firstName = firstName;
          user.lastName = lastName;
          user.displayName = dispName;
          user.deptName = deptName;
          user.jwtExpireSec = jwtExpireSec;

          localStorage.setItem("user", axiosUtil.jsonbigStringfy(user));
          setCurrentUser(user);

          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t(response.data.message) || t("MSG_SaveSuccess") });
        },
        (error) => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(error.response.data.message) || t("MSG_SaveFailed") });
          debug(error);
        }
      );
  }

  const resetPassword = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
  }

  const saveUserPassword = () => {
    userService.changePassword(username, oldPassword, newPassword, confirmPassword)
      .then(
        () => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
          resetPassword();
        },
        (error) => {
          const { message, details } = error.response.data;

          let errorMsg = message ? t(message) : t("MSG_SaveFailed");
          if (details?.count && (isEqual(message, "MSG_PasswordDCredit") || isEqual(message, "MSG_PasswordLCredit") || isEqual(message, "MSG_PasswordOCredit") ||
            isEqual(message, "MSG_PasswordUCredit") || isEqual(message, "MSG_PasswordRepeat"))) {
            errorMsg = t(message, { N: details.count });
          }
          if (details?.min && details?.max && isEqual(message, "MSG_CheckPasswordLength")) {
            errorMsg = t(message, { min: details.min, max: details.max });
          }

          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: errorMsg });
          debug(error);
        }
      );
  }

  const handleHiddenChange = () => {
    setChangePassword(!changePassword);
  }

  const handleDialog = (action, _falseAction, el) => {
    if (action === "SAVE") {
      if (el.type === "INFO") {
        saveUserInfo();
      } else if (el.type === "PASSWORD") {
        saveUserPassword();
      }
    }
  };

  const roleLookupValue = role => {
    return GridUtils.gridValueL10N(role, { headerName: "userRoles" });
  }

  const getIconByRole = role => {
    if (role.includes("ADMIN")) {
      return <SupervisorAccountIcon />;
    } else if (role.includes("MGR")) {
      return <ManageAccountsIcon />;
    }

    return <PersonIcon />;
  }

  useEffect(() => {
    if (currentUser?.roles && !isEmpty(currentUser.roles)) {
      const roles = currentUser.roles.filter(role => !role.includes("APP"));
      setUserRoles(roles);
    }
  }, [currentUser?.roles]);

  return (
    <>
      <Box className={"wrap-personal-setting"} sx={{ height: "100%", overflow: "auto", px: 3, py: 2 }}>
        <Typography sx={{ fontWeight: 700 }} variant="h5">{t("personalSettings")}</Typography>
        <Divider sx={{ borderColor: "primary.main", borderBottomWidth: 2, mt: 1, mb: 3 }} />

        <Box sx={{ maxWidth: 1160 }}>
          <Grid container spacing={2.5} alignItems="flex-start">
            {/* 좌: 사용자 정보 (+ 비밀번호) */}
            <Grid item xs={12} md={8}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* 사용자 정보 */}
                <AppCard>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography sx={{ ...CARD_HEADER_SX, mb: 2 }}>{t("userInformation")}</Typography>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12}>
                        <TextField fullWidth id="username" name="username" label={t("emailAddress")} value={username} disabled />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth id="lastName" name="lastName" label={t("lastName")}
                          value={lastName} onChange={(e) => setLastName(e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth id="firstName" name="firstName" label={t("firstName")}
                          value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth id="dispName" name="dispName" label={t("displayName")}
                          value={dispName} onChange={(e) => setDispName(e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth id="deptName" name="deptName" label={t("deptName")}
                          value={deptName} onChange={(e) => setDeptName(e.target.value)} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth id="postnName" name="postnName" label={t("postnName")}
                          value={currentUser?.postnName ? currentUser?.postnName : ""} disabled />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth id="siteName" name="siteName" label={t("siteName")}
                          value={currentUser?.siteName ? currentUser?.siteName : ""} disabled />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SelectExpTime loginExpTime={jwtExpireSec} setLoginExpTime={setJwtExpireSec} fullWidth />
                      </Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />
                    <FormControlLabel
                      sx={{ ml: 0, mr: 0 }}
                      control={<StyledSwitch size="small" checked={changePassword} onChange={handleHiddenChange} color="primary" />}
                      label={<Typography variant="body2" sx={{ ml: 1 }}>{t("changePassword")}</Typography>}
                    />
                  </CardContent>
                </AppCard>

                {/* Save 버튼 — 카드 밖(사용자 정보 카드 아래) */}
                <Box>
                  <Button size="large" color="primary" variant="contained" disableElevation startIcon={<SaveIcon />}
                    onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm"), el: { type: "INFO" } })}>{t("save")}</Button>
                </Box>

                {/* 비밀번호 (토글 시) */}
                {changePassword && (
                  <AppCard>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography sx={{ ...CARD_HEADER_SX, mb: 2 }}>{t("password")}</Typography>
                      <Box component="form">
                        <Grid container spacing={2.5}>
                          <Grid item xs={12}>
                            <PasswordInput
                              name="oldPassword"
                              label={t("currentPassword")}
                              password={oldPassword}
                              setPassword={setOldPassword} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <PasswordInput
                              name="newPassword"
                              label={t("newPassword")}
                              password={newPassword}
                              setPassword={setNewPassword}
                              showPassword={showPassword}
                              setShowPassword={setShowPassword}
                              withVisibilityToggle />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <PasswordInput
                              name="confirmPassword"
                              label={t("newPasswordConfirmation")}
                              password={confirmPassword}
                              setPassword={setConfirmPassword}
                              showPassword={showPassword}
                              setShowPassword={setShowPassword}
                              withVisibilityToggle />
                          </Grid>
                        </Grid>
                        <Box sx={{ mt: 2.5 }}>
                          <PasswordPolicyContainer />
                        </Box>
                        <Box sx={{ mt: 2.5 }}>
                          <Button size="large" color="primary" variant="contained" disableElevation startIcon={<SaveIcon />}
                            onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm"), el: { type: "PASSWORD" } })}>{t("save")}</Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </AppCard>
                )}
              </Box>
            </Grid>

            {/* 우: 권한 + 차트 팔레트 */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <AppCard>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography sx={{ ...CARD_HEADER_SX, mb: 2 }}>{t("authority")}</Typography>
                    <Stack spacing={1.25}>
                      {userRoles?.map((role, idx) => (
                        <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Icon sx={{ color: grey[600], display: "flex" }}>{getIconByRole(role)}</Icon>
                          <Typography variant="body2">{roleLookupValue(role)}</Typography>
                        </Box>
                      ))}
                      {isEmpty(userRoles) && (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </Stack>
                  </CardContent>
                </AppCard>

                {/* 차트 팔레트 — 전역 적용(선택 즉시 저장) */}
                <AppCard>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography sx={{ ...CARD_HEADER_SX, mb: 0.5 }}>{t("chartPalette")}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t("chartPaletteDesc")}</Typography>
                    <Stack spacing={1}>
                      {ColorUtil.PALETTE_OPTIONS.map((opt) => (
                        <Box key={opt.id} onClick={() => choosePalette(opt.id)}
                          sx={{
                            display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: 1.5, cursor: "pointer",
                            border: 1, borderColor: chartPalette === opt.id ? "primary.main" : "divider",
                            bgcolor: chartPalette === opt.id ? "action.selected" : "transparent",
                            transition: "background-color .15s, border-color .15s",
                            "&:hover": { bgcolor: "action.hover" },
                          }}>
                          <Radio size="small" checked={chartPalette === opt.id} sx={{ p: 0.25 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{t(opt.labelKey)}</Typography>
                            <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                              {opt.colors.map((c, i) => (
                                <Box key={i} sx={{ width: 16, height: 16, borderRadius: 0.5, bgcolor: c, boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)" }} />
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>{t("chartPaletteApplyNote")}</Typography>
                  </CardContent>
                </AppCard>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        el={dialogInfo.el}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(PersonalSettings);